/**
 * Consult Scheduling — ADMIN (host-side) store seams.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS
 * ─────────────────────────────────────────────────────────────────────────
 *  The staff-facing counterpart to the public Door B page: a LIVE listener on
 *  the team-only `consultations` collection for the admin "Consultations" list.
 *  Same resilient pattern as the rest of the portal (`consultStore.ts` /
 *  `flags.ts`): lazy-import Firebase, emit a safe default immediately, live-
 *  update on snapshot, keep the default on any error, return an unsubscribe.
 *
 *  DEFENSIVE READ: the booking Cloud Functions own the `consultations/{id}` doc
 *  shape, and Door A / Door B may spell fields slightly differently. So the
 *  mapper below tolerates several field spellings (mirrors
 *  `consultStore.fetchBookedSlotStarts`) and never throws on a partial doc — a
 *  read hiccup must degrade to a calm empty list, never a crash.
 * ─────────────────────────────────────────────────────────────────────────
 */

/** Normalised consultation status for the admin list. */
export type AdminConsultStatus =
  | 'pending'
  | 'confirmed'
  | 'rescheduled'
  | 'cancelled'
  | 'no_show'
  | 'done'
  | 'unknown';

/** Which front door produced the booking. */
export type AdminConsultSource = 'door_a_staff' | 'door_b_selfserve' | 'unknown';

/** The row shape the admin list renders (a defensive VIEW of the doc). */
export interface AdminConsultation {
  id: string;
  clientName: string;
  email: string | null;
  phone: string | null;
  /** Slot start/end as ISO instants (anchored to `timeZone`). */
  startISO: string | null;
  endISO: string | null;
  /** The anchored bookable-truth zone (IST). */
  timeZone: string;
  /** The client's own display zone, when Door B stored it. */
  clientTz: string | null;
  status: AdminConsultStatus;
  source: AdminConsultSource;
  meetUrl: string | null;
  typeName: string | null;
  /** When superseded by a reschedule, the new consultation id. */
  rescheduledToId: string | null;
}

const STATUS_SET = new Set<AdminConsultStatus>([
  'pending',
  'confirmed',
  'rescheduled',
  'cancelled',
  'no_show',
  'done',
]);

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null;
}

function pick(r: Record<string, unknown>, ...keys: string[]): string | null {
  for (const k of keys) {
    const v = str(r[k]);
    if (v) return v;
  }
  return null;
}

/** PURE — map a raw Firestore doc into the admin row shape. Exported for tests. */
export function mapConsultationDoc(
  id: string,
  raw: Record<string, unknown>,
): AdminConsultation {
  const client = (raw.client ?? {}) as Record<string, unknown>;
  const type = (raw.type ?? {}) as Record<string, unknown>;

  const first = pick(client, 'firstName', 'first') ?? '';
  const last = pick(client, 'lastName', 'last') ?? '';
  const clientName =
    `${first} ${last}`.trim() ||
    pick(raw, 'clientName', 'name') ||
    pick(client, 'name') ||
    'Unnamed contact';

  const rawStatus = pick(raw, 'status') ?? 'unknown';
  const status: AdminConsultStatus = STATUS_SET.has(rawStatus as AdminConsultStatus)
    ? (rawStatus as AdminConsultStatus)
    : 'unknown';

  const rawSource = pick(raw, 'source');
  const source: AdminConsultSource =
    rawSource === 'door_a_staff' || rawSource === 'door_b_selfserve'
      ? rawSource
      : 'unknown';

  return {
    id,
    clientName,
    email: pick(client, 'email') ?? pick(raw, 'email'),
    phone: pick(client, 'phone') ?? pick(raw, 'phone'),
    startISO: pick(raw, 'startISO', 'slotStartISO', 'slotStartIso', 'slotStart'),
    endISO: pick(raw, 'endISO', 'slotEndISO', 'slotEndIso', 'slotEnd'),
    timeZone: pick(raw, 'timeZone', 'timezone') ?? 'Asia/Kolkata',
    clientTz: pick(raw, 'clientTz', 'clientTimeZone'),
    status,
    source,
    meetUrl: pick(raw, 'googleMeetUrl', 'meetUrl', 'meetLink'),
    typeName: pick(type, 'name') ?? pick(raw, 'typeName'),
    rescheduledToId: pick(raw, 'rescheduledToId'),
  };
}

/** Honest feed state — distinguishes "loading" from a genuinely empty list. */
export type ConsultationsFeed =
  | { phase: 'loading'; items: AdminConsultation[] }
  | { phase: 'ready'; items: AdminConsultation[] }
  | { phase: 'error'; items: AdminConsultation[] };

/**
 * Subscribe to the `consultations` collection (team-only read per rules). Emits
 * `{ phase:'loading' }` immediately, then `ready` with the live, defensively-
 * mapped list (sorted by start ascending, nulls last), or `error` (keeping the
 * last-good list) if the snapshot or SDK fails. Reads the whole collection (no
 * server orderBy → no composite index needed), mirroring `fetchBookedSlotStarts`.
 * Returns an unsubscribe fn.
 */
export function listenConsultations(
  cb: (feed: ConsultationsFeed) => void,
): () => void {
  cb({ phase: 'loading', items: [] });
  let unsub: (() => void) | null = null;
  let cancelled = false;
  let lastGood: AdminConsultation[] = [];
  void (async () => {
    try {
      const [{ db }, { collection, onSnapshot }] = await Promise.all([
        import('../../../lib/firebase'),
        import('firebase/firestore'),
      ]);
      if (cancelled) return;
      unsub = onSnapshot(
        collection(db, 'consultations'),
        (snap) => {
          const rows = snap.docs.map((d) =>
            mapConsultationDoc(d.id, d.data() as Record<string, unknown>),
          );
          rows.sort((a, b) => {
            if (!a.startISO) return 1;
            if (!b.startISO) return -1;
            return a.startISO.localeCompare(b.startISO);
          });
          lastGood = rows;
          cb({ phase: 'ready', items: rows });
        },
        () => cb({ phase: 'error', items: lastGood }),
      );
    } catch {
      // SDK unavailable / read blocked → honest error, not a silent empty.
      if (!cancelled) cb({ phase: 'error', items: [] });
    }
  })();
  return () => {
    cancelled = true;
    if (unsub) unsub();
  };
}
